/**
 * South African Grade Phase Definitions
 *
 * Derives grade phases from grade numbers per Department of Basic Education structure.
 * Foundation Phase: Grade R-3
 * Intermediate Phase: Grade 4-6
 * Senior Phase: Grade 7-9
 * FET Phase: Grade 10-12
 */

export const GRADE_PHASES = {
  foundation: { label: 'Foundation Phase', grades: ['R', '0', '1', '2', '3'] },
  intermediate: { label: 'Intermediate Phase', grades: ['4', '5', '6'] },
  senior: { label: 'Senior Phase', grades: ['7', '8', '9'] },
  fet: { label: 'FET Phase', grades: ['10', '11', '12'] },
};

export const PHASE_OPTIONS = Object.entries(GRADE_PHASES).map(([key, config]) => ({
  value: key,
  label: config.label,
  grades: config.grades,
}));

/**
 * Get the phase key for a given grade value (e.g., "7" → "senior")
 * Handles Grade R stored as "R", "0", or "Grade R"
 */
export function getPhaseForGrade(gradeValue) {
  const normalized = String(gradeValue).replace(/^grade\s*/i, '').trim();
  for (const [phase, config] of Object.entries(GRADE_PHASES)) {
    if (config.grades.includes(normalized)) return phase;
  }
  return null;
}

/**
 * Get grade IDs that belong to a given phase.
 * @param {string} phase - Phase key (foundation, intermediate, senior, fet)
 * @param {Array} allGrades - Grade documents from gradesService [{id, value, label, ...}]
 * @returns {string[]} Array of grade document IDs
 */
export function getGradeIdsForPhase(phase, allGrades) {
  const phaseGrades = GRADE_PHASES[phase]?.grades || [];
  return allGrades
    .filter(g => {
      const val = String(g.value || g.label || g.id).replace(/^grade\s*/i, '').trim();
      return phaseGrades.includes(val);
    })
    .map(g => g.id);
}

/**
 * Get all phases that a set of grade IDs belong to.
 * @param {string[]} gradeIds - Grade document IDs
 * @param {Array} allGrades - Grade documents from gradesService
 * @returns {string[]} Array of phase keys
 */
export function getPhasesForGradeIds(gradeIds, allGrades) {
  const phases = new Set();
  const gradeSet = new Set(gradeIds);
  for (const grade of allGrades) {
    if (gradeSet.has(grade.id)) {
      const phase = getPhaseForGrade(grade.value || grade.label || grade.id);
      if (phase) phases.add(phase);
    }
  }
  return [...phases];
}
